import Step1 from "./Step1";
import Step2 from "./Step2";
import Step3 from "./Step3";
import Step4 from "./Step4";

import { Formik, Form } from "formik";
import Cookies from "universal-cookie";
import * as Yup from "yup";
import React, { useState } from "react";
import { JobsApi } from "../../../../service";
import { Box, Typography } from "@mui/material";
import StepperForm from "../../common/StepperForm";
import useApiHandler from "../../../hooks/ApiHandler";
import { MODAL_STATES } from "../../../constant/Home/Jobs";
import { useCurrentSelectedData } from "../../../context/CurrentSelectedDataContext";
import { useStatisticsData } from "../../../context/StatisticsData";
import {
  validationSchemas,
  Title,
  formFields,
  stepsNames,
  stepFieldsMap,
} from "../../../constant/Home/ActiveJobs";

const warrantyStepNames = [
  "Confirm Job Information",
  "Confirm Warranty Completion",
  "Upload Supporting Media",
  "Confirm Job Details",
];

const warrantyTitles = {
  0: Title[0],
  1: {
    title: "No Additional Billing Required",
    subTitle: "This completion will close out the approved warranty claim without generating a new invoice.",
  },
  2: Title[2],
  3: Title[3],
};

const warrantyStepFieldsMap = {
  ...stepFieldsMap,
  1: [],
};

const warrantyPricingValidationSchema = Yup.object().shape({
  material_cost: Yup.number().nullable().min(0, "Must be a positive number"),
  labor_cost: Yup.number().nullable().min(0, "Must be a positive number"),
  job_cost: Yup.number().nullable().min(0, "Must be a positive number"),
  line_items: Yup.array().nullable().notRequired(),
});

const WarrantyPricingStep = () => (
  <Box
    sx={{
      border: "1px solid #ECECEC",
      borderRadius: "16px",
      backgroundColor: "#FAFAFA",
      padding: { md: 4, xs: 3 },
      display: "flex",
      flexDirection: "column",
      gap: 2,
    }}
  >
    <Typography sx={{ color: "#242424", fontSize: "20px", fontWeight: 500 }}>
      Approved warranty work
    </Typography>
    <Typography sx={{ color: "#595959", fontSize: "16px", lineHeight: 1.6 }}>
      Properteasy will not create a second invoice for this completion. You can continue to upload any supporting
      media in the next step and then submit the completion to close out the warranty claim.
    </Typography>
  </Box>
);

const CompleteJobs = ({ setModalState, getActiveJobs }) => {
  const [activeStep, setActiveStep] = useState(0);
  const { currentSelectedData, setCurrentSelectedData } =
    useCurrentSelectedData();
  const { callApi, loading } = useApiHandler();
  const cookies = new Cookies();
  const token = cookies.get("token");
  const { refreshStatistics } = useStatisticsData();
  const isWarrantyJob =
    String(
      currentSelectedData?.activeWarrantyClaim?.status ||
        currentSelectedData?.active_warranty_claim?.status ||
        ""
    ).toUpperCase() === "APPROVED";
  
  // Normalize incoming values for display in Step 1
  const formatCity = (city) => {
    if (!city) return "";
    const withSpaces = String(city).replace(/_/g, " ");
    return withSpaces
      .split(" ")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ");
  };

  const formatState = (state) => {
    if (!state) return "";
    return String(state).trim().toUpperCase();
  };
  const initialValues = {
    job_number: currentSelectedData?.jobNumber || "",
    date_completed: currentSelectedData?.availableTo || "",
    property_address: currentSelectedData?.propertyAddress?.streetAddress || "",
    property_city: formatCity(currentSelectedData?.propertyAddress?.city) || "",
    property_state: formatState(currentSelectedData?.propertyAddress?.state) || "",
    property_zip: currentSelectedData?.propertyAddress?.zipCode || "",
    material_cost: 0,
    labor_cost: 0,
    job_cost: 0,
    line_items: [],
    jobMedia: null,
    pricing_model: "",
  };

  const activeSteps = {
    0: <Step1 fields={formFields.step1} />,
    1: isWarrantyJob ? <WarrantyPricingStep /> : <Step2 fields={formFields.step2} />,
    2: <Step3 fields={formFields.step3} />,
    3: <Step4 />,
  };

  const validationSchema =
    activeStep === 1 && isWarrantyJob
      ? warrantyPricingValidationSchema
      : validationSchemas[activeStep];

  const resolvedStepsNames = isWarrantyJob ? warrantyStepNames : stepsNames;
  const resolvedTitles = isWarrantyJob ? warrantyTitles : Title;
  const resolvedStepFieldsMap = isWarrantyJob ? warrantyStepFieldsMap : stepFieldsMap;

  const handleSubmit = async (values, { setSubmitting }) => {
    console.log("Form submitted with values:", values);

    // Determine pricing_model based on validation schema logic
    const hasItemizedCosts =
      values.material_cost != null &&
      values.material_cost > 0 &&
      values.labor_cost != null &&
      values.labor_cost > 0;
    const hasJobCost = values.job_cost != null && values.job_cost > 0;

    // Set pricing_model based on provided costs
    const pricing_model = hasItemizedCosts
      ? "MATERIALS_LABOR"
      : hasJobCost
      ? "FLAT_RATE"
      : null;

    const updatedValues = isWarrantyJob
      ? {
          ...values,
          material_cost: 0,
          labor_cost: 0,
          job_cost: 0,
          line_items: [],
          pricing_model: null,
        }
      : {
          ...values,
          pricing_model,
        };
    console.log("updatedValues", updatedValues);
    try {
      await callApi(
        () =>
          JobsApi.completeJob(
            { ...updatedValues, id: currentSelectedData.id },
            token
          ),
        {
          onSuccess: async (response) => {
            if (response.data) {
              setSubmitting(false);
              setCurrentSelectedData({
                ...currentSelectedData,
                ...response.data,
                date_completed: updatedValues.date_completed,
              });
              // Navigate to Step 5 (completion screen)
              setModalState(MODAL_STATES.JOB_SUMMARY);
              getActiveJobs();
              refreshStatistics();
            }
          },
          onError: (error) => {
            console.error("API error:", error);
            setSubmitting(false);
            // Optionally maintain current step
            setActiveStep((prevActiveStep) => prevActiveStep);
          },
        }
      );
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const paddingXValue = activeStep > 0 ? 2 : 7;
  return (
    <div className="w-full">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ errors, touched }) => (
          <Form>
            <StepperForm
              stepsNames={resolvedStepsNames}
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              children={activeSteps[activeStep]}
              footerText={false}
              errors={errors}
              touched={touched}
              stepFieldsMap={resolvedStepFieldsMap}
              submitStep={3}
              position="relative"
            >
              <Box
                sx={{
                  borderRadius: "16px",
                  paddingX: paddingXValue,
                  gap: 2,
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  "@media (max-width: 768px)": {
                    paddingX: 0,
                  },
                }}
              >
                {activeStep > 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      justifyContent: "center",
                      alignItems: "center",
                      paddingY: { md: 3, xs: 2 },
                    }}
                  >
                    <Typography
                      variant="h1"
                      sx={{
                        color: "#242424",
                        lineHeight: { md: "22px" },
                        textAlign: "left",
                      }}
                    >
                      {resolvedTitles[activeStep]?.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#595959",
                        fontSize: { md: "20px", xs: "16px" },
                        textAlign: "center",
                        lineHeight: { md: "22px" },
                      }}
                    >
                      {resolvedTitles[activeStep]?.subTitle}
                    </Typography>
                  </Box>
                )}
                {activeSteps[activeStep]}
              </Box>
            </StepperForm>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default CompleteJobs;
